import Link from 'next/link';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';

export const BFLandingFooter = ({ isPostBlackFriday = false }: { isPostBlackFriday?: boolean }) => {
  const { t } = useTranslation();

  const { isMobile } = useWindowSize();

  const [currentDeviceOS, setCurrentDeviceOS] = useState('iOS');

  useEffect(() => {
    const whatBrowser = () => {
      // eslint-disable-next-line unicorn/better-regex
      if (/Android/i.test(navigator?.userAgent)) {
        setCurrentDeviceOS('android');
      }
      setCurrentDeviceOS('iOS');
    };
    whatBrowser();
  }, []);

  const downloadAppText = isPostBlackFriday
    ? ''
    : t(`blackFriday.downloadTheGoatApp`, `Download the GOAT app to play`);

  return (
    <>
      <BFLandingFooter.FooterGradient />
      <BFLandingFooter.BottomContainer>
        <BFLandingFooter.InnerContainer>
          <BFLandingFooter.DownloadAppCopy data-qa="bf_download_app_copy">
            {downloadAppText}
          </BFLandingFooter.DownloadAppCopy>
        </BFLandingFooter.InnerContainer>
        <BFLandingFooter.InnerContainer>
          <BFLandingFooter.DownloadAppLinkContainer>
            <Link
              href="https://goat.app.link/?$desktop_deepview=goat_bf_24"
              passHref
              prefetch={false}
              legacyBehavior
            >
              <BFLandingFooter.DownloadAppBorderedLink
                data-qa="bf_download_app_link"
                $hide={currentDeviceOS !== 'iOS' && isMobile}
              >
                <BFLandingFooter.CTAImage
                  src="/images/apple-app-store-cta.png"
                  sizes="70px"
                  alt="Download IOS App CTA"
                />
              </BFLandingFooter.DownloadAppBorderedLink>
            </Link>
            <Link
              href="https://goat.app.link/?$desktop_deepview=goat_bf_24"
              passHref
              prefetch={false}
              legacyBehavior
            >
              <BFLandingFooter.DownloadAppBorderedLink
                data-qa="bf_download_app_link"
                $hide={currentDeviceOS !== 'android' && isMobile}
              >
                <BFLandingFooter.CTAImage
                  src="/images/cropped-android-badge.png"
                  sizes="70px"
                  alt="Download Android App CTA"
                />
              </BFLandingFooter.DownloadAppBorderedLink>
            </Link>
          </BFLandingFooter.DownloadAppLinkContainer>
        </BFLandingFooter.InnerContainer>
      </BFLandingFooter.BottomContainer>
    </>
  );
};

BFLandingFooter.TitleAsset = styled.div`
  display: flex;
  justify-content: flex-start;

  video {
    height: 52px;

    @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
      height: 64px;
    }
  }
`;

BFLandingFooter.BottomContainer = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  bottom: 0;
  display: flex;
  justify-content: space-between;
  left: 0;
  padding: 0px 16px 16px 16px;
  position: fixed;
  right: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.blackFridayLandingFooter};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 0px 32px 30px 32px;
  }
`;

BFLandingFooter.FooterGradient = styled.div`
  background: linear-gradient(0deg, #000000 0%, rgba(0, 0, 0, 0) 100%);
  height: 24px;
  position: fixed;
  bottom: 56px;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.blackFridayLandingFooter};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    bottom: 70px;
  }
`;

BFLandingFooter.InnerContainer = styled.div<{ $column?: boolean }>`
  display: flex;

  ${({ $column }) => $column && `flex-direction: column;`}
`;

BFLandingFooter.DownloadAppCopy = styled.div`
  align-items: center;
  font-family: GOATProduct-MonoRegular;
  font-size: 12px;
  display: flex;
  justify-content: flex-end;
  line-height: 14px;
  margin-right: 24px;
  margin-top: 4px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    font-size: 12.5px;
    line-height: 15px;
    margin-top: 0px;
  }
`;

BFLandingFooter.ContestOverCopy = styled.div`
  margin-bottom: 35px;
  max-width: 272px;
  text-align: center;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
`;

BFLandingFooter.DownloadAppLinkContainer = styled.div`
  display: flex;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    a:first-child {
      margin-right: 16px;
    }
  }
`;

BFLandingFooter.DownloadAppBorderedLink = styled.a<{ $hide: boolean }>`
  align-items: center;
  display: flex;
  height: 40px;
  justify-content: center;
  text-transform: uppercase;
  ${variableFontRegular({ size: '9.5px', height: '14px' })};

  ${({ $hide }) => $hide && `display: none;`}
`;

BFLandingFooter.LearnMoreLink = styled.a`
  text-decoration: underline;
  text-transform: capitalize;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
`;

BFLandingFooter.CTAImage = styled.img`
  max-height: 32px;
  width: auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-height: 40px;
  }
`;
